// Collision + damage resolution (plan §4.4 order). All hot loops use the enemy spatial
// hash for broad-phase and squared-distance narrow-phase, with reused query buffers
// (no per-frame allocation). dealDamage() is the single chokepoint for crit + splash.

import { ARENA, HIT_FLASH_SEC, MAX_ENEMY_RADIUS, PLAYER } from './balance';
import type { Pool } from '../core/pool';
import type { Rng } from '../core/rng';
import type { SpatialHash } from '../core/spatialHash';
import type { Enemy } from './enemies';
import type { Modifiers } from './passives';
import type { Projectile } from './weapons';
import type { PlayerState } from './world';
import { spawnBeam, spawnDamageNumber } from './fx';
import type { Beam, DamageNumber } from './fx';
import { firstBoulderHit, hitsBoulder } from './boulders';
import type { Boulder } from './boulders';

/** Fraction of non-crit hits that show a floating number (crits always show). */
const DMGNUM_SAMPLE = 0.12;

/** Shared per-frame context for any code that deals damage. */
export interface HitCtx {
  rng: Rng;
  mods: Modifiers;
  enemies: Pool<Enemy>;
  hash: SpatialHash;
  numbers: Pool<DamageNumber>; // floating damage-number pool
  beams: Pool<Beam>; // lightning-arc pool (chain weapons)
  boulders: Boulder[]; // static obstacles
  boulderHash: SpatialHash; // static boulder lookup
  boulderBuf: number[]; // scratch for boulder queries
  vineWalls: Boulder[]; // activated temporary arena walls
  vineHash: SpatialHash;
  vineBuf: number[];
  queryBuf: number[]; // broad-phase scratch for the caller
  splashBuf: number[]; // separate scratch so splash queries don't clobber the caller
  // Ability-driven player damage multipliers (Berserk etc.), refreshed each tick.
  playerX: number;
  playerY: number;
  dmgMul: number; // global player damage multiplier (1 = none)
  dmgMulClose: number; // multiplier for hits within closeRadius2 of the player
  closeRadius2: number; // squared point-blank radius
  damageFieldMul: (x: number, y: number) => number;
  damageDealt: number; // running total of damage dealt this run (for the score)
}

const flash = (e: Enemy): void => {
  if (e.hitFlashSec < HIT_FLASH_SEC) e.hitFlashSec = HIT_FLASH_SEC;
};

/**
 * Apply damage to one enemy: roll crit, then optionally splash a fraction to nearby
 * enemies. The single place crit/splash live so every weapon benefits.
 */
export function dealDamage(
  ctx: HitCtx,
  e: Enemy,
  baseDamage: number,
  srcX: number,
  srcY: number,
  allowSplash: boolean,
): void {
  // Ability damage multiplier (Berserk): global, with a stronger point-blank bonus.
  let mul = ctx.dmgMul;
  if (ctx.dmgMulClose > mul) {
    const px = srcX - ctx.playerX;
    const py = srcY - ctx.playerY;
    if (px * px + py * py < ctx.closeRadius2) mul = ctx.dmgMulClose;
  }
  let dmg = baseDamage * mul * ctx.damageFieldMul(srcX, srcY);
  let crit = false;
  if (ctx.rng.next() < ctx.mods.critChance) {
    dmg *= ctx.mods.critMult;
    crit = true;
  }
  e.hp -= dmg;
  ctx.damageDealt += dmg;
  flash(e);
  if (crit || ctx.rng.next() < DMGNUM_SAMPLE) {
    spawnDamageNumber(ctx.numbers, e.x, e.y, Math.round(dmg), crit);
  }

  if (allowSplash && ctx.mods.splashFrac > 0 && ctx.mods.splashRadius > 0) {
    const r = ctx.mods.splashRadius;
    const sd = dmg * ctx.mods.splashFrac;
    const n = ctx.hash.query(srcX, srcY, r + MAX_ENEMY_RADIUS, ctx.splashBuf);
    for (let i = 0; i < n; i++) {
      const o = ctx.enemies.items[ctx.splashBuf[i]];
      if (o === e || o.hp <= 0) continue;
      const dx = o.x - srcX;
      const dy = o.y - srcY;
      const rr = r + o.radius;
      if (dx * dx + dy * dy <= rr * rr) {
        o.hp -= sd;
        ctx.damageDealt += sd;
        flash(o);
      }
    }
  }
}

/** Move projectiles, cull expired/out-of-bounds, and apply damage (pierce + status). */
export function updateProjectiles(projectiles: Pool<Projectile>, dt: number, ctx: HitCtx): void {
  const enemies = ctx.enemies;
  for (let i = 0; i < projectiles.count; i++) {
    const p = projectiles.items[i];
    p.prevX = p.x;
    p.prevY = p.y;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.lifeSec -= dt;

    if (p.lifeSec <= 0) {
      burstProjectile(p, ctx);
      splitProjectile(p, projectiles);
      projectiles.releaseAt(i);
      i--;
      continue;
    }
    if (p.x < 0 || p.y < 0 || p.x > ARENA.width || p.y > ARENA.height) {
      projectiles.releaseAt(i);
      i--;
      continue;
    }
    // Prism attacks bounce only from permanent terrain. Temporary walls stay a readable
    // blocker instead of becoming an unbounded ricochet surface.
    const boulder = firstBoulderHit(p.x, p.y, p.radius, ctx.boulders, ctx.boulderHash, ctx.boulderBuf);
    if (boulder !== null && p.terrainBounces > 0) {
      bounceProjectile(p, boulder, ctx);
      continue;
    }
    if (boulder !== null || hitsBoulder(p.x, p.y, p.radius, ctx.vineWalls, ctx.vineHash, ctx.vineBuf)) {
      projectiles.releaseAt(i);
      i--;
      continue;
    }

    const n = ctx.hash.query(p.x, p.y, p.radius + MAX_ENEMY_RADIUS, ctx.queryBuf);
    for (let j = 0; j < n; j++) {
      const e = enemies.items[ctx.queryBuf[j]];
      if (e.hp <= 0 || e.lastProjHit === p.id) continue; // each projectile hits an enemy once
      const dx = e.x - p.x;
      const dy = e.y - p.y;
      const rr = e.radius + p.radius;
      if (dx * dx + dy * dy <= rr * rr) {
        e.lastProjHit = p.id;
        dealDamage(ctx, e, p.damage, e.x, e.y, true);
        if (p.slowSec > 0) {
          e.slowSec = Math.max(e.slowSec, p.slowSec);
          e.slowMul = Math.min(e.slowMul, p.slowMul);
        }
        if (p.burnSec > 0) {
          e.burnSec = Math.max(e.burnSec, p.burnSec);
          e.burnDps = Math.max(e.burnDps, p.burnDps);
        }
        if (--p.pierceLeft < 0) {
          projectiles.releaseAt(i);
          i--;
          break;
        }
      }
    }
  }
}

function bounceProjectile(p: Projectile, boulder: Boulder, ctx: HitCtx): void {
  const dx = p.x - boulder.x;
  const dy = p.y - boulder.y;
  const dist = Math.hypot(dx, dy);
  const speed = Math.hypot(p.vx, p.vy) || 1;
  const nx = dist > 0.001 ? dx / dist : -p.vx / speed;
  const ny = dist > 0.001 ? dy / dist : -p.vy / speed;
  const dot = p.vx * nx + p.vy * ny;
  p.vx -= 2 * dot * nx;
  p.vy -= 2 * dot * ny;
  p.x = boulder.x + nx * (boulder.radius + p.radius + 0.25);
  p.y = boulder.y + ny * (boulder.radius + p.radius + 0.25);
  p.terrainBounces--;
  triggerBounceChains(p, ctx);
}

function triggerBounceChains(p: Projectile, ctx: HitCtx): void {
  if (p.bounceChainCount <= 0 || p.bounceChainDamage <= 0 || p.bounceChainRange <= 0) return;
  const token = -(p.id + 1);
  const range = p.bounceChainRange * (p.railChargedSec > 0 ? 1.45 : 1);
  let x = p.x;
  let y = p.y;
  for (let link = 0; link < p.bounceChainCount; link++) {
    const n = ctx.hash.query(x, y, range + MAX_ENEMY_RADIUS, ctx.splashBuf);
    let best: Enemy | null = null;
    let bestD2 = range * range;
    for (let i = 0; i < n; i++) {
      const candidate = ctx.enemies.items[ctx.splashBuf[i]];
      if (candidate.hp <= 0 || candidate.chainMark === token) continue;
      const dx = candidate.x - x;
      const dy = candidate.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = candidate;
      }
    }
    if (best === null) return;
    best.chainMark = token;
    spawnBeam(
      ctx.beams,
      x,
      y,
      best.x,
      best.y,
      p.railChargedSec > 0 ? 0xb8ffff : p.color,
      'straight',
    );
    dealDamage(ctx, best, p.bounceChainDamage, best.x, best.y, true);
    x = best.x;
    y = best.y;
  }
}

function splitProjectile(p: Projectile, projectiles: Pool<Projectile>): void {
  if (p.splitCount <= 0 || p.splitDamage <= 0) return;
  const base = Math.atan2(p.vy, p.vx);
  const speed = Math.hypot(p.vx, p.vy) * 0.82;
  const spread = p.splitCount === 1 ? 0 : 1.08 / (p.splitCount - 1);
  for (let i = 0; i < p.splitCount; i++) {
    const shard = projectiles.acquire();
    if (shard === null) return;
    const angle = base + (i - (p.splitCount - 1) * 0.5) * spread;
    shard.x = p.x;
    shard.y = p.y;
    shard.prevX = p.x;
    shard.prevY = p.y;
    shard.vx = Math.cos(angle) * speed;
    shard.vy = Math.sin(angle) * speed;
    shard.radius = Math.max(2.5, p.radius * 0.68);
    shard.damage = p.splitDamage;
    shard.lifeSec = 0.54;
    shard.color = p.color;
    shard.pierceLeft = 0;
    shard.id = p.id * 16 + i + 1;
    shard.slowMul = p.slowMul;
    shard.slowSec = p.slowSec;
    shard.burnDps = p.burnDps;
    shard.burnSec = p.burnSec;
    shard.burstDamage = 0;
    shard.burstRadius = 0;
    shard.terrainBounces = 0;
    shard.splitCount = 0;
    shard.splitDamage = 0;
    shard.bounceChainCount = 0;
    shard.bounceChainDamage = 0;
    shard.bounceChainRange = 0;
    shard.railChargedSec = p.railChargedSec;
    shard.sprite = p.sprite;
  }
}

function burstProjectile(p: Projectile, ctx: HitCtx): void {
  if (p.burstDamage <= 0 || p.burstRadius <= 0) return;
  const n = ctx.hash.query(p.x, p.y, p.burstRadius + MAX_ENEMY_RADIUS, ctx.splashBuf);
  for (let i = 0; i < n; i++) {
    const e = ctx.enemies.items[ctx.splashBuf[i]];
    if (e.hp <= 0) continue;
    const dx = e.x - p.x;
    const dy = e.y - p.y;
    const rr = e.radius + p.burstRadius;
    if (dx * dx + dy * dy <= rr * rr) dealDamage(ctx, e, p.burstDamage, e.x, e.y, false);
  }
}

/**
 * Apply enemy contact damage to the player, respecting i-frames. Returns true if the
 * player was hit this tick (for juice). Must run BEFORE dead enemies are reaped so the
 * spatial-hash indices are still valid (plan §4.4 ordering).
 */
export function applyContactDamage(
  enemies: Pool<Enemy>,
  hash: SpatialHash,
  player: PlayerState,
  buf: number[],
  dt: number,
  invuln: boolean,
  dmgMul: number,
  onHit?: (enemy: Enemy) => void,
): boolean {
  if (player.iFrameSec > 0) {
    player.iFrameSec -= dt;
    return false;
  }
  if (invuln) return false; // ability shield (Berserk / Aegis)
  const n = hash.query(player.x, player.y, player.radius + MAX_ENEMY_RADIUS, buf);
  for (let j = 0; j < n; j++) {
    const e = enemies.items[buf[j]];
    if (e.hp <= 0) continue;
    const dx = e.x - player.x;
    const dy = e.y - player.y;
    const rr = e.radius + player.radius;
    if (dx * dx + dy * dy <= rr * rr) {
      player.hp -= e.contactDmg * dmgMul;
      player.iFrameSec = PLAYER.iFramesSec;
      onHit?.(e);
      return true;
    }
  }
  return false;
}
